import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Divider,
  Breadcrumbs,
  Link,
  Snackbar
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';

// Services
import { getWarehouses } from '../../services/warehouseService';
import { getParts } from '../../services/partService';
import { 
  getWarehouseReorders, 
  bulkUpdateWarehouseReorders,
  calculateReorderPoints
} from '../../services/warehouseReorderService';
import { getPartTechnicianRequirements } from '../../services/partTechnicianRequirementService';

const WarehouseReorderBulkUpdate = () => {
  const navigate = useNavigate();
  
  // State
  const [warehouses, setWarehouses] = useState([]);
  const [parts, setParts] = useState([]);
  const [reorders, setReorders] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [partFilter, setPartFilter] = useState('');
  const [techRequirements, setTechRequirements] = useState({});
  
  // Fetch warehouses and parts on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetchLoading(true);
        
        // Fetch warehouses
        const warehousesData = await getWarehouses();
        setWarehouses(warehousesData);
        
        // Fetch parts
        const partsData = await getParts();
        setParts(partsData);
        
        // Fetch technician requirements
        const requirementsData = await getPartTechnicianRequirements();
        const requirementsMap = {};
        requirementsData.forEach(req => {
          requirementsMap[req.part_id] = req.quantity_per_technician;
        });
        setTechRequirements(requirementsMap);
        
        setFetchLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
        setFetchLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Fetch reorder points when warehouse is selected
  useEffect(() => {
    const fetchReorders = async () => {
      if (!selectedWarehouse) return;
      
      try {
        setLoading(true);
        
        // Fetch reorder points for selected warehouse
        const reordersData = await getWarehouseReorders({ warehouse_id: selectedWarehouse });
        
        // Create a map of existing reorder points
        const reorderMap = {};
        reordersData.forEach(reorder => {
          reorderMap[reorder.part_id] = {
            id: reorder.id,
            reorder_point: reorder.reorder_point,
            target_stock_level: reorder.target_stock_level
          };
        });
        
        // Create reorder data for all parts
        const allReorders = parts.map(part => {
          const existing = reorderMap[part.id];
          return {
            part_id: part.id,
            part_number: part.internal_part_number,
            description: part.description,
            reorder_point: existing ? existing.reorder_point : (part.reorder_point || 0),
            target_stock_level: existing ? existing.target_stock_level : null,
            existing: Boolean(existing),
            id: existing ? existing.id : null
          };
        });
        
        setReorders(allReorders);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching reorder points:', err);
        setError('Failed to load reorder points. Please try again.');
        setLoading(false);
      }
    };
    
    fetchReorders();
  }, [selectedWarehouse, parts]);
  
  // Handle warehouse selection change
  const handleWarehouseChange = (e) => {
    setSelectedWarehouse(e.target.value);
  };
  
  // Handle reorder point change
  const handleReorderChange = (partId, field, value) => {
    const numValue = parseInt(value, 10) || 0;
    
    setReorders(prevReorders => 
      prevReorders.map(reorder => 
        reorder.part_id === partId 
          ? { ...reorder, [field]: numValue } 
          : reorder
      )
    );
  };
  
  // Filter parts based on search term
  const filteredReorders = reorders.filter(reorder => 
    reorder.part_number.toLowerCase().includes(partFilter.toLowerCase()) ||
    reorder.description.toLowerCase().includes(partFilter.toLowerCase())
  );
  
  // Handle save
  const handleSave = async () => {
    if (!selectedWarehouse) {
      setError('Please select a warehouse');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Prepare data for bulk update
      const reordersData = reorders.map(reorder => ({
        part_id: reorder.part_id,
        reorder_point: reorder.reorder_point,
        target_stock_level: reorder.target_stock_level
      }));
      
      // Send bulk update request
      await bulkUpdateWarehouseReorders(selectedWarehouse, reordersData);
      
      setSuccess('Reorder points updated successfully');
      setLoading(false);
      
      // Refresh data
      const updatedReorders = await getWarehouseReorders({ warehouse_id: selectedWarehouse });
      
      // Update reorder data
      const reorderMap = {};
      updatedReorders.forEach(reorder => {
        reorderMap[reorder.part_id] = {
          id: reorder.id,
          reorder_point: reorder.reorder_point,
          target_stock_level: reorder.target_stock_level
        };
      });
      
      setReorders(prevReorders => 
        prevReorders.map(reorder => {
          const updated = reorderMap[reorder.part_id];
          return updated ? {
            ...reorder,
            reorder_point: updated.reorder_point,
            target_stock_level: updated.target_stock_level,
            existing: true,
            id: updated.id
          } : reorder;
        })
      );
    } catch (err) {
      console.error('Error saving reorder points:', err);
      setError(err.message || 'Failed to save reorder points. Please try again.');
      setLoading(false);
    }
  };
  
  // Handle calculate
  const handleCalculate = async () => {
    if (!selectedWarehouse) {
      setError('Please select a warehouse');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Send calculate request
      const result = await calculateReorderPoints(selectedWarehouse);
      
      setSuccess('Reorder points calculated successfully');
      
      // Refresh data
      const updatedReorders = await getWarehouseReorders({ warehouse_id: selectedWarehouse });
      
      // Update reorder data
      const reorderMap = {};
      updatedReorders.forEach(reorder => {
        reorderMap[reorder.part_id] = {
          id: reorder.id,
          reorder_point: reorder.reorder_point,
          target_stock_level: reorder.target_stock_level
        };
      });
      
      setReorders(prevReorders => 
        prevReorders.map(reorder => {
          const updated = reorderMap[reorder.part_id];
          return updated ? {
            ...reorder,
            reorder_point: updated.reorder_point,
            target_stock_level: updated.target_stock_level,
            existing: true,
            id: updated.id
          } : reorder;
        })
      );
      
      setLoading(false);
    } catch (err) {
      console.error('Error calculating reorder points:', err);
      setError(err.message || 'Failed to calculate reorder points. Please try again.');
      setLoading(false);
    }
  };
  
  if (fetchLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          component={RouterLink}
          to="/"
        >
          Dashboard
        </Link>
        <Link
          color="inherit"
          component={RouterLink}
          to="/inventory"
        >
          Inventory
        </Link>
        <Typography color="text.primary">
          Bulk Update Reorder Points
        </Typography>
      </Breadcrumbs>
      
      {/* Page title */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Bulk Update Warehouse Reorder Points
        </Typography>
      </Box>
      
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Success message */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      
      {/* Warehouse selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="warehouse-label">Warehouse</InputLabel>
              <Select
                labelId="warehouse-label"
                value={selectedWarehouse}
                onChange={handleWarehouseChange}
                label="Warehouse"
              >
                <MenuItem value="">
                  <em>Select Warehouse</em>
                </MenuItem>
                {warehouses.map(warehouse => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.name} - {warehouse.technician_count} Technicians
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<CalculateIcon />}
                onClick={handleCalculate}
                disabled={!selectedWarehouse || loading}
              >
                Calculate from Technician Count
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={!selectedWarehouse || loading}
              >
                Save All Changes
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Reorder points table */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
          <TextField
            label="Filter Parts"
            variant="outlined"
            size="small"
            value={partFilter}
            onChange={(e) => setPartFilter(e.target.value)}
            sx={{ mr: 2, width: 300 }}
          />
          <Typography variant="body2" color="text.secondary">
            {filteredReorders.length} of {reorders.length} parts shown
          </Typography>
        </Box>
        
        <Divider />
        
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Part Number</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Reorder Point</TableCell>
                <TableCell align="right">Target Stock Level</TableCell>
                <TableCell align="center">Per Technician</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedWarehouse ? (
                filteredReorders.length > 0 ? (
                  filteredReorders.map((reorder) => (
                    <TableRow key={reorder.part_id}>
                      <TableCell>{reorder.part_number}</TableCell>
                      <TableCell>{reorder.description}</TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          value={reorder.reorder_point}
                          onChange={(e) => handleReorderChange(reorder.part_id, 'reorder_point', e.target.value)}
                          InputProps={{ inputProps: { min: 0 } }}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          value={reorder.target_stock_level || ''}
                          onChange={(e) => handleReorderChange(reorder.part_id, 'target_stock_level', e.target.value)}
                          InputProps={{ inputProps: { min: 0 } }}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {techRequirements[reorder.part_id] ? 
                          techRequirements[reorder.part_id] : 
                          <Typography variant="body2" color="text.secondary">Not set</Typography>
                        }
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No parts match your filter criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Please select a warehouse to view and edit reorder points
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Back button */}
      <Box sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to="/inventory"
        >
          Back to Inventory
        </Button>
      </Box>
    </Container>
  );
};

export default WarehouseReorderBulkUpdate;
